import { useState } from 'react'
import { Modal, Form, Input, Select, Button, AutoComplete, message } from 'antd'
import VoiceTextarea from './VoiceTextarea'
import PhoneInput from './PhoneInput'
import { SendOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import axiosInstance from '@/lib/axios'

interface Props {
  open: boolean
  onCancel: () => void
  onSent: () => void
}

export function ComposeMessageModal({ open, onCancel, onSent }: Props) {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [customerSearch, setCustomerSearch] = useState('')

  const { data: customers } = useQuery({
    queryKey: ['customers-search', customerSearch],
    queryFn: async () => {
      if (customerSearch.length < 2) return []
      const res = await axiosInstance.get('/customers/', {
        params: { search: customerSearch, limit: 20 },
      })
      return res.data.items ?? []
    },
    enabled: customerSearch.length >= 2,
  })

  const handleSelectCustomer = (_value: string, option: any) => {
    form.setFieldsValue({
      destinatario_numero: option.cellulare || option.telefono || '',
      destinatario_nome: option.nome,
      destinatario_cognome: option.cognome,
      customer_id: option.id,
    })
  }

  const handleCancel = () => {
    form.resetFields()
    setCustomerSearch('')
    onCancel()
  }

  const handleSubmit = async (values: any) => {
    setLoading(true)
    try {
      await axiosInstance.post('/comunicazioni/invia', {
        destinatario_numero: values.destinatario_numero,
        destinatario_nome: values.destinatario_nome || null,
        destinatario_cognome: values.destinatario_cognome || null,
        customer_id: values.customer_id || null,
        testo: values.testo,
        tipo: values.tipo || null,
      })
      message.success('Messaggio inviato')
      form.resetFields()
      setCustomerSearch('')
      onSent()
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } }
      message.error(err.response?.data?.detail || 'Errore durante l\'invio')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      title="Nuovo messaggio"
      open={open}
      onCancel={handleCancel}
      footer={null}
      width="min(520px, 95vw)"
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit} style={{ marginTop: 8 }}>
        <Form.Item label="Cerca cliente (opzionale)">
          <AutoComplete
            options={(customers ?? []).map((c: any) => ({
              value: `${c.nome ?? ''} ${c.cognome ?? ''}`.trim(),
              label: `${c.nome ?? ''} ${c.cognome ?? ''} — ${c.cellulare || c.telefono || 'nessun numero'}`,
              ...c,
            }))}
            onSearch={setCustomerSearch}
            onSelect={handleSelectCustomer}
            placeholder="Scrivi nome cliente..."
            allowClear
          />
        </Form.Item>

        <Form.Item
          name="destinatario_numero"
          label="Numero destinatario"
          rules={[{ required: true, message: 'Inserisci il numero di telefono' }]}
        >
          <PhoneInput placeholder="Numero di telefono" />
        </Form.Item>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Form.Item name="destinatario_nome" label="Nome">
            <Input placeholder="Mario" />
          </Form.Item>
          <Form.Item name="destinatario_cognome" label="Cognome">
            <Input placeholder="Rossi" />
          </Form.Item>
        </div>

        <Form.Item name="customer_id" hidden><Input /></Form.Item>

        <Form.Item name="tipo" label="Canale">
          <Select placeholder="Automatico (WhatsApp → SMS)" allowClear>
            <Select.Option value="whatsapp">WhatsApp</Select.Option>
            <Select.Option value="sms">SMS</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="testo"
          label="Testo messaggio"
          rules={[{ required: true, message: 'Scrivi il testo del messaggio' }]}
        >
          <VoiceTextarea rows={4} minHeight={100} maxHeight={250} placeholder="Testo del messaggio..." label="Testo messaggio" compact />
        </Form.Item>

        <Form.Item style={{ marginBottom: 0 }}>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={handleCancel}>Annulla</Button>
            <Button type="primary" htmlType="submit" loading={loading} icon={<SendOutlined />}>
              Invia
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  )
}
